import api from './authService';

export const externalApiService = {
  // ============================================
  // API KEYS CRUD
  // ============================================

  // Get all API keys for current company
  getApiKeys: async (params = {}) => {
    try {
      const { include_inactive = false, page = 1, limit = 20 } = params;

      // Cache curto (30s) + deduplicação por chave
      if (!externalApiService._listCache) externalApiService._listCache = new Map();
      const key = `list:${include_inactive}:${page}:${limit}`;
      const now = Date.now();
      const cached = externalApiService._listCache.get(key);
      if (cached && cached.expiresAt > now) return cached.data;
      if (cached?.inflight) return await cached.inflight;

      const inflight = api.get('/api-keys', { params: { include_inactive, page, limit } })
        .then(res => {
          externalApiService._listCache.set(key, { data: res.data, expiresAt: now + 30000, inflight: null });
          return res.data;
        })
        .catch(err => {
          const c = externalApiService._listCache.get(key);
          if (c) externalApiService._listCache.set(key, { ...c, inflight: null });
          throw err;
        });

      externalApiService._listCache.set(key, { data: null, expiresAt: 0, inflight });
      return await inflight;
    } catch (error) {
      throw error;
    }
  },

  // Get API key by ID
  getApiKey: async (keyId) => {
    try {
      const response = await api.get(`/api-keys/${keyId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get decrypted API key for code generation
  getDecryptedApiKey: async (keyId) => {
    try {
      const response = await api.get(`/api-keys/${keyId}/decrypted`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new API key
  createApiKey: async (keyData) => {
    try {
      const response = await api.post('/api-keys', keyData);
      if (externalApiService._listCache) externalApiService._listCache.clear();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update API key
  updateApiKey: async (keyId, updates) => {
    try {
      const response = await api.put(`/api-keys/${keyId}`, updates);
      if (externalApiService._listCache) externalApiService._listCache.clear();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get API keys that allow access to a specific agent
  getApiKeysByAgent: async (agentName, params = {}) => {
    try {
      const { include_inactive = false } = params;
      const response = await api.get(`/api-keys/by-agent/${encodeURIComponent(agentName)}`, {
        params: { include_inactive }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 🔄 NOVO: Get simplified API keys list for webhook dropdown
  getApiKeysForDropdown: async () => {
    try {
      console.log('🔍 Buscando API keys para dropdown...');
      
      // Usar chamada direta da API (igual à função getApiKeys)
      const response = await api.get('/api-keys', {
        params: { 
          include_inactive: false, 
          page: 1, 
          limit: 100 
        }
      });
      
      console.log('📡 Resposta da API:', response);
      console.log('📊 Dados da resposta:', response.data);
      
      // Extrair array de API keys da resposta - estrutura: {items: Array, ...}
      const result = response.data;
      const apiKeys = result?.items || result?.api_keys || [];
      
      console.log('🔑 API Keys extraídas:', apiKeys);
      console.log('📏 Quantidade encontrada:', apiKeys.length);
      console.log('🔍 Estrutura da resposta:', Object.keys(result));
      
      if (Array.isArray(apiKeys) && apiKeys.length > 0) {
        console.log('📋 Primeira API key:', apiKeys[0]);
        
        // Retornar apenas os dados necessários para o dropdown
        const mappedKeys = apiKeys.map(key => ({
          id: key.id,
          key_name: key.key_name,
          key_preview: key.key_preview || 'N/A'
        }));
        
        console.log('✅ API Keys mapeadas:', mappedKeys);
        return mappedKeys;
      } else {
        console.warn('⚠️ Nenhuma API key encontrada ou estrutura inesperada');
        return [];
      }
    } catch (error) {
      console.error('❌ Erro ao buscar API keys para dropdown:', error);
      console.error('📄 Status:', error.response?.status);
      console.error('📄 Detalhes do erro:', error.response?.data);
      return [];  // Retornar array vazio em caso de erro
    }
  },



  // Delete API key
  deleteApiKey: async (keyId) => {
    try {
      await api.delete(`/api-keys/${keyId}`);
      if (externalApiService._listCache) externalApiService._listCache.clear();
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  // Regenerate API key (create new secret, keep config)
  regenerateApiKey: async (keyId) => {
    try {
      const response = await api.post(`/api-keys/${keyId}/regenerate`);
      if (externalApiService._listCache) externalApiService._listCache.clear();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // API KEY USAGE & STATISTICS
  // ============================================

  // Get usage statistics for specific API key
  getApiKeyUsage: async (keyId, days = 30) => {
    try {
      const response = await api.get(`/api-keys/${keyId}/usage`, {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get usage logs for API key with filters
  getApiKeyLogs: async (keyId, filters = {}) => {
    try {
      const { 
        page = 1, 
        limit = 50, 
        status_code, 
        agent_name, 
        channel,
        start_date,
        end_date 
      } = filters;
      
      const response = await api.get(`/api-keys/${keyId}/logs`, {
        params: { 
          page, 
          limit, 
          status_code, 
          agent_name, 
          channel,
          start_date,
          end_date
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get company-wide external API statistics
  getCompanyStats: async (days = 30) => {
    try {
      const response = await api.get('/external-api/stats', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // RATE LIMIT TIERS
  // ============================================

  // Get all available rate limit tiers
  getRateLimitTiers: async (params = {}) => {
    try {
      const { include_inactive = false, include_system = true, page = 1, limit = 50 } = params;
      const response = await api.get('/rate-limit-tiers', {
        params: { include_inactive, include_system, page, limit }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get rate limit tier by ID
  getRateLimitTier: async (tierId) => {
    try {
      const response = await api.get(`/rate-limit-tiers/${tierId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create custom rate limit tier
  createRateLimitTier: async (tierData) => {
    try {
      const response = await api.post('/rate-limit-tiers', tierData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update rate limit tier
  updateRateLimitTier: async (tierId, updates) => {
    try {
      const response = await api.put(`/rate-limit-tiers/${tierId}`, updates);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete rate limit tier
  deleteRateLimitTier: async (tierId, force = false) => {
    try {
      await api.delete(`/rate-limit-tiers/${tierId}`, {
        params: { force }
      });
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  // Set tier as default
  setDefaultTier: async (tierId) => {
    try {
      const response = await api.post(`/rate-limit-tiers/${tierId}/set-default`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get tier usage statistics
  getTierUsageStats: async () => {
    try {
      const response = await api.get('/rate-limit-tiers/stats/usage');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // CONFIGURATION HELPERS
  // ============================================

  // Get available channels for API keys
  getAvailableChannels: async () => {
    try {
      const response = await api.get('/api-keys/config/channels');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get available agents for API keys
  getAvailableAgents: async () => {
    try {
      const response = await api.get('/api-keys/config/agents');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get tier validation rules
  getTierValidationRules: async () => {
    try {
      const response = await api.get('/rate-limit-tiers/config/validation');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // CODE GENERATOR
  // ============================================

  // Get API keys that can access specific agent
  getApiKeysForAgent: async (agentName) => {
    try {
      const response = await api.get(`/agents/${agentName}/api-keys`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Validate agent+key+channel combination
  validateAgentAccess: async (agentName, keyId, channel) => {
    try {
      const response = await api.get(`/agents/${agentName}/validate-access`, {
        params: { key_id: keyId, channel }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Generate code snippets for integration
  generateCodeSnippet: (config) => {
    const { 
      apiKey, 
      agentName, 
      endUserId, 
      message, 
      channel = 'api',
      language = 'curl',
      baseUrl = process.env.REACT_APP_EXTERNAL_API_BASE_URL || 
        (process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || 'https://api.organia.com.br')
    } = config;

    const payload = {
      agent_name: agentName,
      end_user_id: endUserId,
      message: message,
      channel: channel
    };

    switch (language) {
      case 'curl':
        return this._generateCurlSnippet(baseUrl, apiKey, payload);
      case 'python':
        return this._generatePythonSnippet(baseUrl, apiKey, payload);
      case 'javascript':
        return this._generateJavaScriptSnippet(baseUrl, apiKey, payload);
      case 'nodejs':
        return this._generateNodeJSSnippet(baseUrl, apiKey, payload);
      default:
        return this._generateCurlSnippet(baseUrl, apiKey, payload);
    }
  },

  // ============================================
  // PRIVATE HELPER METHODS
  // ============================================

  _generateCurlSnippet: (baseUrl, apiKey, payload) => {
    return `curl -X POST "${baseUrl}/external/v1/chat" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(payload, null, 2)}'`;
  },

  _generatePythonSnippet: (baseUrl, apiKey, payload) => {
    return `import requests

response = requests.post(
    "${baseUrl}/external/v1/chat",
    headers={
        "Authorization": "Bearer ${apiKey}",
        "Content-Type": "application/json"
    },
    json=${JSON.stringify(payload, null, 4)}
)

if response.status_code == 200:
    data = response.json()
    print(f"Resposta do agente: {data['message']}")
    print(f"Thread ID: {data['thread_id']}")
    print(f"Tokens usados: {data['tokens_used']}")
elif response.status_code == 429:
    print("Rate limit excedido. Aguarde e tente novamente.")
    retry_after = response.headers.get("Retry-After")
    if retry_after:
        print(f"Tente novamente em {retry_after} segundos")
else:
    print(f"Erro: {response.status_code} - {response.text}")`;
  },

  _generateJavaScriptSnippet: (baseUrl, apiKey, payload) => {
    return `// Usando fetch (navegador)
const response = await fetch('${baseUrl}/external/v1/chat', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ${apiKey}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(${JSON.stringify(payload, null, 2)})
});

if (response.ok) {
  const data = await response.json();
  console.log('Resposta do agente:', data.message);
  console.log('Thread ID:', data.thread_id);
  console.log('Tokens usados:', data.tokens_used);
} else if (response.status === 429) {
  console.log('Rate limit excedido');
  const retryAfter = response.headers.get('Retry-After');
  if (retryAfter) {
    console.log(\`Tente novamente em \${retryAfter} segundos\`);
  }
} else {
  console.error('Erro:', response.status, await response.text());
}`;
  },

  _generateNodeJSSnippet: (baseUrl, apiKey, payload) => {
    return `// Node.js com axios
const axios = require('axios');

async function callAgent() {
  try {
    const response = await axios.post('${baseUrl}/external/v1/chat', 
      ${JSON.stringify(payload, null, 6)},
      {
        headers: {
          'Authorization': 'Bearer ${apiKey}',
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('Resposta do agente:', response.data.message);
    console.log('Thread ID:', response.data.thread_id);
    console.log('Tokens usados:', response.data.tokens_used);
    
    return response.data;
  } catch (error) {
    if (error.response?.status === 429) {
      console.log('Rate limit excedido');
      const retryAfter = error.response.headers['retry-after'];
      if (retryAfter) {
        console.log(\`Tente novamente em \${retryAfter} segundos\`);
      }
    } else {
      console.error('Erro:', error.response?.status, error.response?.data);
    }
    throw error;
  }
}

callAgent();`;
  },

  // ============================================
  // UTILITY METHODS
  // ============================================

  // Format API key for display (mask secret part)
  formatApiKey: (apiKey) => {
    if (!apiKey) return '';
    
    const prefix = apiKey.substring(0, 8); // pk_live_ or pk_test_
    const suffix = apiKey.substring(apiKey.length - 4);
    const stars = '*'.repeat(Math.max(0, apiKey.length - 12));
    
    return `${prefix}${stars}${suffix}`;
  },

  // Get tier display info with color coding
  getTierDisplayInfo: (tier) => {
    const tierInfo = {
      dev: { color: 'grey', icon: '🔧', label: 'Desenvolvimento' },
      basic: { color: 'blue', icon: '🌱', label: 'Básico' },
      pro: { color: 'purple', icon: '🚀', label: 'Profissional' },
      enterprise: { color: 'gold', icon: '👑', label: 'Empresarial' }
    };

    return tierInfo[tier?.tier_name] || { 
      color: 'default', 
      icon: '⚙️', 
      label: tier?.display_name || 'Personalizado' 
    };
  },

  // Format usage statistics for display
  formatUsageStats: (stats) => {
    return {
      ...stats,
      success_rate: stats.total_requests > 0 
        ? ((stats.successful_requests / stats.total_requests) * 100).toFixed(1)
        : '0.0',
      avg_response_time_formatted: `${stats.avg_response_time || 0}ms`,
      total_credits_formatted: `R$ ${(stats.total_credits || 0).toFixed(4)}`
    };
  },

  // Estatísticas
  getApiKeyStats: async (keyId, days = 30) => {
    try {
      const response = await api.get(`/api-keys/${keyId}/stats?days=${days}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getCompanyApiStats: async (days = 30) => {
    try {
      const response = await api.get(`/api-keys/stats?days=${days}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // WEBHOOK STATISTICS
  // ============================================

  // Get webhook stats
  getWebhookStats: async () => {
    try {
      const response = await api.get('/webhooks/stats/summary');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Validate IP whitelist format
  validateIpWhitelist: (ipList) => {
    if (!ipList || ipList.length === 0) return { valid: true, errors: [] };
    
    const errors = [];
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\/(?:[0-9]|[1-2][0-9]|3[0-2]))?$/;
    
    ipList.forEach((ip, index) => {
      if (!ipRegex.test(ip.trim())) {
        errors.push(`IP/CIDR inválido na linha ${index + 1}: ${ip}`);
      }
    });
    
    return { valid: errors.length === 0, errors };
  },

  // ============================================
  // WEBHOOKS CRUD
  // ============================================

  // Get all webhooks for current company
  getWebhooks: async (params = {}) => {
    try {
      const { include_inactive = false } = params;
      const response = await api.get('/webhooks', {
        params: { include_inactive }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get webhook by ID
  getWebhook: async (webhookId) => {
    try {
      const response = await api.get(`/webhooks/${webhookId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new webhook
  createWebhook: async (webhookData) => {
    try {
      const response = await api.post('/webhooks', webhookData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update webhook
  updateWebhook: async (webhookId, webhookData) => {
    try {
      const response = await api.put(`/webhooks/${webhookId}`, webhookData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete webhook
  deleteWebhook: async (webhookId) => {
    try {
      const response = await api.delete(`/webhooks/${webhookId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Test webhook
  testWebhook: async (webhookId) => {
    try {
      const response = await api.post(`/webhooks/${webhookId}/test`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get webhook deliveries
  getWebhookDeliveries: async (webhookId, params = {}) => {
    try {
      const { limit = 50, status_filter } = params;
      const response = await api.get(`/webhooks/${webhookId}/deliveries`, {
        params: { limit, status_filter }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get webhook stats
  getWebhookStats: async () => {
    try {
      const response = await api.get('/webhooks/stats/summary');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};
